import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface TenantInfo {
  id: string;
  tenant_id: string;
  display_name?: string | null;
  admin_email?: string | null;
  status: string;
  tier?: string;
  created_at?: string;
  last_activity?: string | null;
}

interface TenantData {
  tenants: TenantInfo[];
  total: number;
}

function statusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'var(--sp-ok)';
    case 'trial':
      return 'var(--sp-warn)';
    case 'churned':
    case 'suspended':
      return 'var(--sp-error)';
    default:
      return 'var(--sp-text-dim)';
  }
}

function TenantClusterMapWidget({ context }: WidgetProps) {
  const [data, setData] = useState<TenantData | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  useEffect(() => {
    if (!context.isDirector) return;

    // Try the admin users/tenants endpoint
    void engineFetchJson<TenantData>('/admin/tenants')
      .then((r) => {
        const response = r;
        setData({
          tenants: Array.isArray(response.tenants) ? response.tenants : [],
          total: typeof response.total === 'number' ? response.total : 0,
        });
        setEndpointMissing(false);
      })
      .catch(() => {
        setEndpointMissing(true);
      });
  }, [context.authToken, context.isDirector]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Tenant Cluster Map</span>
        <a href="/command" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Command Center
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        {endpointMissing ? (
          <div>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Awaiting engine endpoint
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginBottom: 8 }}>
              GET /api/v1/admin/tenants
            </div>
            {/* Mock cluster visualization */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginTop: 8 }}>
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    borderRadius: '50%',
                    background: i < 3 ? 'var(--sp-ok)' : i < 7 ? 'var(--sp-warn)' : 'var(--sp-border)',
                    opacity: 0.3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: 'var(--sp-text-dim)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--sp-ok)', opacity: 0.5 }} /> active
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--sp-warn)', opacity: 0.5 }} /> trial
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--sp-border)' }} /> inactive
              </span>
            </div>
          </div>
        ) : data ? (
          <div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 22, fontWeight: 700 }}>{data.total}</span>
              <span style={{ fontSize: 12, color: 'var(--sp-text-muted)', marginLeft: 6 }}>
                tenant{data.total === 1 ? '' : 's'}
              </span>
            </div>

            {data.tenants.length > 0 ? (
              <div style={{ maxHeight: 260, overflow: 'auto' }}>
                {data.tenants.map((t) => (
                  <div
                    key={t.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '5px 0',
                      fontSize: 12,
                      borderBottom: '1px solid var(--sp-border)',
                      minWidth: 0,
                    }}
                    title={`${t.display_name || t.tenant_id || t.id} · ${t.status}${t.tier ? ` · ${t.tier}` : ''}`}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: statusColor(t.status),
                        flexShrink: 0,
                      }}
                      title={t.status}
                    />
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.display_name || t.admin_email || t.tenant_id || t.id}
                      </div>
                    </div>
                    {t.tier && (
                      <span
                        className="mono"
                        style={{
                          fontSize: 9,
                          color: 'var(--sp-text-dim)',
                          textTransform: 'uppercase',
                          flexShrink: 0,
                        }}
                      >
                        {t.tier}
                      </span>
                    )}
                    <span
                      className="mono"
                      style={{ fontSize: 10, color: 'var(--sp-text-dim)', flexShrink: 0 }}
                    >
                      {t.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>No tenants found.</div>
            )}
          </div>
        ) : (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading tenants...</div>
        )}
      </div>
    </div>
  );
}

export const tenantClusterMapWidget: WidgetDefinition = {
  id: 'tenant-cluster-map',
  name: 'Tenant Cluster Map',
  description: 'Visual map of all tenants with status and tier indicators.',
  icon: 'map',
  category: 'admin',
  scope: 'director',
  defaultSize: { w: 12, h: 5 },
  minSize: { w: 6, h: 4 },
  component: TenantClusterMapWidget,
};
